import { MissingRequiredRentalLedger } from 'app/constants/error_codes'
import { translateErrorCodeToMessage } from 'app/constants/error_messages'

interface SubmitErrorResponseMetadataItem {
  applicantGUID?: string
  rentalHistoryGUID?: string
}

interface SubmitErrorResponseMetadata {
  missing?: SubmitErrorResponseMetadataItem[]
}

interface SubmitErrorResponse {
  code?: string
  message?: string
  app_code?: number | string
  metadata?: SubmitErrorResponseMetadata
}

const getOptionalString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined

// Matches the TEMP patch in translateErrorCodeToMessage that suppresses
// "please login" session-expired errors to avoid showing raw error toasts
const isSuppressedLoginError = (text: string): boolean =>
  typeof text === 'string' &&
  text.includes('please login to perform this action')

const getOptionalAppCode = (
  value: unknown,
): SubmitErrorResponse['app_code'] | undefined =>
  typeof value === 'number' || typeof value === 'string' ? value : undefined

const getOptionalMetadataItem = (
  value: unknown,
): SubmitErrorResponseMetadataItem | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const valueRecord = value as Record<string, unknown>
  const applicantGUID = getOptionalString(valueRecord.applicantGUID)
  const rentalHistoryGUID = getOptionalString(valueRecord.rentalHistoryGUID)

  if (!applicantGUID && !rentalHistoryGUID) {
    return null
  }

  const item: SubmitErrorResponseMetadataItem = {}
  if (applicantGUID) {
    item.applicantGUID = applicantGUID
  }
  if (rentalHistoryGUID) {
    item.rentalHistoryGUID = rentalHistoryGUID
  }

  return item
}

const getOptionalMetadata = (
  value: unknown,
): SubmitErrorResponseMetadata | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined
  }

  const valueRecord = value as Record<string, unknown>
  const missingRaw = valueRecord.missing
  if (!Array.isArray(missingRaw)) {
    return undefined
  }

  const missing = missingRaw
    .map((item) => getOptionalMetadataItem(item))
    .filter((item): item is SubmitErrorResponseMetadataItem => Boolean(item))

  if (!missing.length) {
    return undefined
  }

  return { missing }
}

const parseSubmitErrorResponse = (
  rawErrorText: string,
): SubmitErrorResponse | null => {
  if (!rawErrorText) {
    return null
  }

  try {
    const parsed = JSON.parse(rawErrorText) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }

    const parsedRecord = parsed as Record<string, unknown>
    const response: SubmitErrorResponse = {}
    const code = getOptionalString(parsedRecord.code)
    const message = getOptionalString(parsedRecord.message)
    const appCode = getOptionalAppCode(parsedRecord.app_code)
    const metadata = getOptionalMetadata(parsedRecord.metadata)

    if (code !== undefined) {
      response.code = code
    }
    if (message !== undefined) {
      response.message = message
    }
    if (appCode !== undefined) {
      response.app_code = appCode
    }
    if (metadata !== undefined) {
      response.metadata = metadata
    }

    return response
  } catch {
    return null
  }
}

export interface SubmitFinalApplicationError {
  message: string
  code?: string
  appCode?: number | string
  metadata?: SubmitErrorResponseMetadata
}

const missingRequiredRentalLedgerFallbackMessage = translateErrorCodeToMessage(
  MissingRequiredRentalLedger,
)

const buildSubmitFinalApplicationError = (
  details: Partial<SubmitFinalApplicationError>,
): SubmitFinalApplicationError => {
  const submitError: SubmitFinalApplicationError = {
    message: details.message || 'Unable to complete request',
  }
  if (details.code !== undefined) {
    submitError.code = details.code
  }
  if (details.appCode !== undefined) {
    submitError.appCode = details.appCode
  }
  if (details.metadata !== undefined) {
    submitError.metadata = details.metadata
  }

  return submitError
}

export const mapSubmitFinalApplicationErrorDetails = (
  rawErrorText: string,
): SubmitFinalApplicationError | null => {
  const parsedError = parseSubmitErrorResponse(rawErrorText)
  const metadataDetails = parsedError?.metadata
    ? { metadata: parsedError.metadata }
    : {}

  if (!parsedError) {
    // Preserve toast suppression for "please login" session-expired errors
    // (mirrors the TEMP patch in translateErrorCodeToMessage)
    if (isSuppressedLoginError(rawErrorText)) {
      return null
    }
    return buildSubmitFinalApplicationError({
      message: translateErrorCodeToMessage(rawErrorText) ?? rawErrorText,
    })
  }

  if (parsedError.code) {
    return buildSubmitFinalApplicationError({
      code: parsedError.code,
      ...metadataDetails,
      message:
        translateErrorCodeToMessage(parsedError.code, parsedError.message) ??
        parsedError.message ??
        rawErrorText,
    })
  }

  if (parsedError.app_code !== undefined) {
    return buildSubmitFinalApplicationError({
      appCode: parsedError.app_code,
      ...metadataDetails,
      message:
        translateErrorCodeToMessage(
          parsedError.app_code,
          parsedError.message,
        ) ??
        parsedError.message ??
        rawErrorText,
    })
  }

  if (parsedError.message) {
    return buildSubmitFinalApplicationError({
      ...metadataDetails,
      message: parsedError.message,
    })
  }

  return buildSubmitFinalApplicationError({
    ...metadataDetails,
    message: translateErrorCodeToMessage(rawErrorText) ?? rawErrorText,
  })
}

export const getSubmitFinalApplicationErrorMessage = (
  error: unknown,
): string => {
  if (!error) {
    return ''
  }
  if (typeof error === 'string') {
    return error
  }
  if (typeof error === 'object' && 'message' in error) {
    return getOptionalString((error as { message?: unknown }).message) || ''
  }
  return String(error)
}

export const isMissingRequiredRentalLedgerSubmitError = (
  error: unknown,
): boolean => {
  if (!error) {
    return false
  }

  if (typeof error === 'string') {
    return error === missingRequiredRentalLedgerFallbackMessage
  }

  if (typeof error !== 'object') {
    return false
  }

  const errorValue = error as {
    code?: unknown
    appCode?: unknown
    message?: unknown
  }

  if (errorValue.code === MissingRequiredRentalLedger) {
    return true
  }

  if (errorValue.appCode === MissingRequiredRentalLedger) {
    return true
  }

  return (
    getOptionalString(errorValue.message) ===
    missingRequiredRentalLedgerFallbackMessage
  )
}

export const getMissingRequiredRentalLedgerHistoryGUIDs = (
  error: unknown,
): string[] => {
  if (!error || typeof error !== 'object' || !('metadata' in error)) {
    return []
  }

  const metadata = (error as { metadata?: SubmitErrorResponseMetadata })
    .metadata
  if (!metadata || !Array.isArray(metadata.missing)) {
    return []
  }

  return metadata.missing
    .map((item) => item.rentalHistoryGUID)
    .filter(
      (guid): guid is string => typeof guid === 'string' && guid.length > 0,
    )
}
